import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { fetchPublicNotice, type PublicNoticeDetail } from '../api/notice';
import { useDarkMode } from '../contexts/DarkModeContext';
import './NoticeTable.css';

/** API: YYYY-MM-DD HH:MM:SS — 그대로 표시 (시간 포함) */
function formatMetaDateTime(value: string | null): string {
  if (!value) return '—';
  return value.trim();
}

function getBadgeClass(category: string): string {
  if (category === '버그수정') return 'bug';
  if (category === '점검안내') return 'notice';
  return 'update';
}

const NoticeDetailPage: React.FC = () => {
  const { noticeId } = useParams<{ noticeId: string }>();
  const { isDarkMode } = useDarkMode();
  const [item, setItem] = useState<PublicNoticeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = Number(noticeId);
    if (!Number.isFinite(id)) {
      setError('잘못된 게시글입니다.');
      setLoading(false);
      return;
    }
    fetchPublicNotice(id)
      .then(setItem)
      .catch((err) => setError(err instanceof Error ? err.message : '조회 실패'))
      .finally(() => setLoading(false));
  }, [noticeId]);

  const category = item?.category || '업데이트';

  return (
    <div className={`notice-container${isDarkMode ? ' dark' : ''}`}>
      <div className="notice-header">
        <div className="notice-header-left">
          <h1>
            <Link to="/notices" className="notice-header-link">
              ← 목록
            </Link>
          </h1>
        </div>
        <Link to="/" className="notice-back">
          Home
        </Link>
      </div>

      {loading && <p className="notice-muted">불러오는 중…</p>}
      {error && <p className="notice-error">{error}</p>}

      {item && (
        <Article>
          <TitleRow>
            <span className={`badge ${getBadgeClass(category)}`}>{category}</span>
            <Title $dark={isDarkMode}>{item.title}</Title>
          </TitleRow>
          <Meta $dark={isDarkMode}>
            <span>{formatMetaDateTime(item.created_at)}</span>
          </Meta>
          <Body $dark={isDarkMode}>{item.content}</Body>
        </Article>
      )}
    </div>
  );
};

export default NoticeDetailPage;

const Article = styled.article`
  width: 100%;
  max-width: none;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.2rem;

  .badge {
    flex: 0 0 auto;
    margin-top: 0.45rem;
  }
`;

const Title = styled.h2<{ $dark: boolean }>`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.45;
  letter-spacing: -0.02em;
  color: ${({ $dark, theme }) => ($dark ? '#f0f0f0' : theme.color222)};

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Meta = styled.div<{ $dark: boolean }>`
  padding: 0 0 2rem;
  margin-bottom: 2.4rem;
  border-bottom: 1px solid ${({ $dark, theme }) => ($dark ? 'rgba(255,255,255,0.12)' : theme.borderGrayColor)};
  font-size: 1.5rem;
  color: ${({ $dark, theme }) => ($dark ? '#c8c8c8' : theme.text666)};
`;

const Body = styled.div<{ $dark: boolean }>`
  white-space: pre-wrap;
  line-height: 1.9;
  font-size: 1.7rem;
  font-weight: 500;
  color: ${({ $dark, theme }) => ($dark ? '#e8e8e8' : theme.color222)};
  padding-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.55rem;
  }
`;
